import React, { useEffect, useMemo, useState } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faEnvelopeOpenText,
  faPaperPlane,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { useAuth } from '../contexts/AuthContext'
import EmailSignaturesPanel from './pr/EmailSignaturesPanel'

type PRTab = 'overview' | 'admin'

type PRToolId = 'emailSignatures' | 'constantContact' | 'postGenerator'

type PRTool = {
  id: PRToolId
  title: string
  subtitle: string
  description: string
  icon: IconDefinition
  /** Shown on the hub card */
  cardHint: string
  kind: 'internal' | 'external'
  externalUrl?: string
  status: 'ready' | 'coming_soon'
  /** Omit from the tools grid (definitions kept for easy restore) */
  hidden?: boolean
}

const PR_TOOLS: PRTool[] = [
  {
    id: 'emailSignatures',
    title: 'Email signatures',
    subtitle: 'Google Workspace',
    description: '',
    cardHint: 'Workspace · Gmail',
    icon: faEnvelopeOpenText,
    kind: 'internal',
    status: 'ready',
  },
  {
    id: 'constantContact',
    title: 'Constant Contact',
    subtitle: 'Email campaigns',
    description:
      'Create and send newsletters, manage lists, and track engagement in Constant Contact. Opens the vendor app in a new browser tab.',
    cardHint: 'Opens in new tab',
    icon: faPaperPlane,
    kind: 'external',
    externalUrl: 'https://login.constantcontact.com/',
    status: 'ready',
    hidden: true,
  },
  {
    id: 'postGenerator',
    title: 'Post generator',
    subtitle: 'Social & announcements',
    description:
      'Draft short posts for internal channels or social templates from approved messaging. This tool is not wired up yet.',
    cardHint: 'Placeholder',
    icon: faWandMagicSparkles,
    kind: 'internal',
    status: 'coming_soon',
    hidden: true,
  },
]

const PR_TOOLS_HUB = PR_TOOLS.filter((t) => !t.hidden)

interface PRAppPageProps {
}

const PRAppPage: React.FC<PRAppPageProps> = () => {
  const { loading: authLoading, hasPermission } = useAuth()

  const showAdmin = hasPermission('prManager')
  const [tab, setTab] = useState<PRTab>('overview')
  const [activeToolId, setActiveToolId] = useState<PRToolId | null>(null)

  useEffect(() => {
    if (tab === 'admin' && !showAdmin) setTab('overview')
  }, [showAdmin, tab])

  useEffect(() => {
    const t = PR_TOOLS.find((x) => x.id === activeToolId)
    if (t?.hidden) setActiveToolId(null)
  }, [activeToolId])

  const activeTool = useMemo(() => PR_TOOLS.find((t) => t.id === activeToolId) ?? null, [activeToolId])

  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    )
  }

  const news = ['PR app ready', 'Pick a tool to get started']
  const recentActivity = ['Email signatures, campaigns, and more in one place']

  const openExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Navbar pageTitle="PR" news={news} recentActivity={recentActivity} />
      <main className="container mx-auto px-4 py-8 flex-1">
        <div className="mx-auto w-full max-w-6xl">
          {showAdmin && (
            <div className="tabs tabs-lift mb-6">
              <input
                type="radio"
                name="pr_tabs"
                role="tab"
                className="tab"
                aria-label="Tools"
                checked={tab === 'overview'}
                onChange={() => {
                  setTab('overview')
                  setActiveToolId(null)
                }}
              />
              <input
                type="radio"
                name="pr_tabs"
                role="tab"
                className="tab"
                aria-label="Admin"
                checked={tab === 'admin'}
                onChange={() => {
                  setTab('admin')
                  setActiveToolId(null)
                }}
              />
            </div>
          )}

          <div>
          {tab === 'overview' && !activeTool && (
            <div className="space-y-8">
              <header className="max-w-2xl">
                <h2 className="text-2xl font-semibold tracking-tight text-base-content">Tools</h2>
                <p className="mt-2 text-base-content/70">
                  Choose a tool below
                </p>
              </header>

              <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 list-none p-0 m-0">
                {PR_TOOLS_HUB.map((tool) => (
                  <li key={tool.id}>
                    <button
                      type="button"
                      onClick={() => setActiveToolId(tool.id)}
                      className="card bg-base-100 border border-base-200 shadow-md hover:shadow-lg hover:border-primary/25 transition-all text-left w-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-base-100"
                    >
                      <div className="card-body p-5 sm:p-6">
                        <div className="flex items-start gap-4">
                          <div
                            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors"
                            aria-hidden
                          >
                            <FontAwesomeIcon icon={tool.icon} className="text-xl" />
                          </div>
                          <div className="min-w-0 flex-1 space-y-2">
                            <h3 className="font-semibold text-base-content leading-tight">{tool.title}</h3>
                            <p className="text-sm text-base-content/60">{tool.subtitle}</p>
                            <p className="text-sm text-base-content/70 line-clamp-2">{tool.cardHint}</p>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tab === 'overview' && activeTool && (
            <div
              className={`mx-auto w-full ${activeTool.id === 'emailSignatures' ? 'max-w-6xl' : 'max-w-3xl'}`}
            >
              <button
                type="button"
                className="btn btn-ghost btn-sm gap-2 -ml-2 mb-6 text-base-content/80"
                onClick={() => setActiveToolId(null)}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="opacity-70" />
                All tools
              </button>

              {activeTool.id === 'emailSignatures' ? (
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary"
                      aria-hidden
                    >
                      <FontAwesomeIcon icon={activeTool.icon} className="text-2xl" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <h2 className="text-2xl font-semibold text-base-content">{activeTool.title}</h2>
                      <p className="text-base-content/60">{activeTool.subtitle}</p>
                    </div>
                  </div>
                  <EmailSignaturesPanel />
                </div>
              ) : (
                <div className="card bg-base-100 border border-base-200 shadow-xl">
                  <div className="card-body p-6 sm:p-8">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div
                        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary"
                        aria-hidden
                      >
                        <FontAwesomeIcon icon={activeTool.icon} className="text-2xl" />
                      </div>
                      <div className="min-w-0 flex-1 space-y-1">
                        <h2 className="card-title text-xl sm:text-2xl">{activeTool.title}</h2>
                        <p className="text-base-content/60">{activeTool.subtitle}</p>
                      </div>
                    </div>

                    <p className="mt-6 text-base-content/80 leading-relaxed">{activeTool.description}</p>

                    <div className="card-actions flex-wrap gap-3 mt-8">
                      {activeTool.kind === 'external' && activeTool.externalUrl && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => openExternal(activeTool.externalUrl!)}
                        >
                          Open Constant Contact
                        </button>
                      )}
                      {activeTool.status === 'coming_soon' && (
                        <button type="button" className="btn btn-disabled btn-outline" disabled>
                          Not available yet
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'admin' && showAdmin && null}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default PRAppPage
